import { ChevronRight, Menu, Search } from "lucide-react";
import { useLocation } from "@tanstack/react-router";
import type { ModuleManifest } from "@praesto/contracts";
import { useCore } from "../lib/core";
import { useCommandRegistry } from "@praesto/core";
import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";

/**
 * Mobile top bar 48px: breadcrumb "Module / Sub" (tap → drawer) +
 * search/Cmd+K + user menu.
 */
export function MobileTopBar({
  activeModule,
  onOpenSubNav,
}: {
  activeModule: ModuleManifest | null;
  onOpenSubNav: () => void;
}) {
  const core = useCore();
  const location = useLocation();
  const { setOpen } = useCommandRegistry();
  const onOpenCommandPalette = () => setOpen(true);
  // pathname like /{tenantSlug}/{moduleId}/{subPath}/...
  const segments = location.pathname.split("/").filter(Boolean);
  const subPath = segments[2] ?? "";

  const moduleLabel = activeModule ? core.i18n.t(`${activeModule.id}.title`) : "Praesto";
  const subLabelKey = activeModule?.subRoutes?.find((s) => subPath === s.path)?.labelKey ?? null;
  const subText = subLabelKey ? core.i18n.t(subLabelKey) : "";

  return (
    <header className="h-12 bg-topbar-bg text-topbar-fg border-b border-border-default flex items-center px-3 gap-2 z-20 flex-shrink-0">
      <button
        onClick={onOpenSubNav}
        className="tap-target -ml-1 flex items-center gap-1.5 text-sm text-fg-default min-w-0 flex-1"
        aria-label={core.i18n.t("shell.action.open_section_menu")}
      >
        <Menu size={20} className="shrink-0 text-fg-default" />
        <span className="font-medium truncate">{moduleLabel}</span>
        {subText && (
          <>
            <ChevronRight size={14} className="text-fg-muted flex-shrink-0" />
            <span className="text-fg-muted truncate">{subText}</span>
          </>
        )}
      </button>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onOpenCommandPalette}
          className="p-1.5 rounded hover:bg-hover"
          aria-label="Cerca"
        >
          <Search size={18} />
        </button>
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}
